import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { FadeUp, Eyebrow, SectionShell, EASE } from "./Reveal";
import { IMAGES, COMPANY } from "../constants/site";

export default function About() {
  return (
    <SectionShell id="about" className="py-24 sm:py-32">
      <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
        <div>
          <FadeUp>
            <Eyebrow>Who we are</Eyebrow>
          </FadeUp>
          <FadeUp delay={0.08}>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-[1.08] text-ink">
              An extension of your firm,
              <span className="text-gradient"> not an agency beside it.</span>
            </h2>
          </FadeUp>
          <FadeUp delay={0.16}>
            <p className="mt-7 text-slate-600 leading-relaxed text-base sm:text-lg">
              TaccTax Solution Pvt. Ltd. is an India-based offshore KPO partner
              built exclusively for US CPA and accounting firms. We integrate
              into your existing workflow — your software, your templates, your
              review process — never disrupt it.
            </p>
            <p className="mt-5 text-slate-500 leading-relaxed">
              Led by {COMPANY.founder}, our team is trained on US GAAP, IRS
              requirements and the platforms your firm already runs on. We work
              across all US time zones and align delivery to your firm's
              deadlines — peak season included.
            </p>
          </FadeUp>
          <FadeUp delay={0.24} className="mt-9 flex flex-wrap gap-3">
            {["US GAAP trained", "IRS-aligned processes", "Review-ready workpapers", "Deadline-matched delivery"].map((chip) => (
              <span key={chip} className="rounded-full border border-line bg-white px-4 py-2 text-xs font-semibold text-slate-600 shadow-soft">
                {chip}
              </span>
            ))}
          </FadeUp>
        </div>

        <FadeUp delay={0.1} className="relative">
          <div className="relative rounded-3xl overflow-hidden border border-line shadow-lift bg-white">
            <motion.img
              src={IMAGES.team}
              alt="TaccTax KPO team at work"
              data-testid="about-team-image"
              initial={{ scale: 1.12 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 1.6, ease: EASE }}
              className="w-full h-[420px] sm:h-[480px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 inset-x-0 p-7">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-orange-300 font-bold">Delivery floor — India</p>
              <p className="mt-2 text-sm text-slate-200 max-w-sm">
                Chartered Accountant supervision on every deliverable, from first entry to final workpaper.
              </p>
            </div>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -4 }}
            whileInView={{ opacity: 1, y: 0, rotate: -3 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, delay: 0.35, ease: EASE }}
            className="absolute -bottom-8 -left-4 sm:-left-8 glass rounded-2xl px-6 py-5 glow-orange"
          >
            <p className="font-display text-3xl font-extrabold text-gradient" data-testid="about-clients-stat">90+</p>
            <p className="mt-1 text-xs font-semibold text-slate-600">US CPA & accounting firms served</p>
          </motion.div>
        </FadeUp>
      </div>

      <FadeUp delay={0.1} className="mt-20 flex justify-center">
        <button
          data-testid="about-services-cta"
          onClick={() => document.querySelector("#services")?.scrollIntoView({ behavior: "smooth" })}
          className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-torange transition-colors cursor-pointer"
        >
          What we take off your desk
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </button>
      </FadeUp>
    </SectionShell>
  );
}
